import { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import type { ProceduralTimelineEvent } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import { useGetMatterUsers } from '@/api/queries/useGetMatterUsers';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { Label } from '@/new/components/shadcn/label';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from '@/new/components/shadcn/multi-select';
import { OUTLOOK_DEEP_LINK_MAX_URL_LENGTH, buildOutlookCalendarUrl } from '@/new/lib/calendar-deep-links';

interface ExportInviteDialogProps {
  event: ProceduralTimelineEvent;
  matterName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExportInviteDialog({ event, matterName, open, onOpenChange }: ExportInviteDialogProps) {
  const { matterId } = useParams();
  const { data: users, isLoading: isLoadingUsers } = useGetMatterUsers(matterId);

  const hasDate = event.startDateTime !== null;
  const userIds = useMemo(() => users?.map((u) => u.id) ?? [], [users]);
  const [recipientIds, setRecipientIds] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setRecipientIds(userIds);
  }, [open, userIds]);

  const recipientEmails = useMemo(() => {
    if (users === undefined) return [];
    const byId = new Map(users.map((u) => [u.id, u.email]));
    return recipientIds.map((id) => byId.get(id)).filter((e): e is string => typeof e === 'string' && e.length > 0);
  }, [users, recipientIds]);

  const wexlerUrl = `${window.location.origin}/matters/${matterId}/procedural-timeline`;

  const handleOpenOutlook = () => {
    if (!hasDate) return;
    const url = buildOutlookCalendarUrl({
      event,
      attendeeEmails: recipientEmails,
      wexlerUrl,
      matterName,
      creatorEmail: event.createdBy.email,
    });
    if (url.length > OUTLOOK_DEEP_LINK_MAX_URL_LENGTH) {
      toast.error('Too many invitees. Please remove some recipients and try again.');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Send calendar invite</DialogTitle>
          <DialogDescription>Share &quot;{event.name}&quot; with your team.</DialogDescription>
        </DialogHeader>

        <div className="mb-2 gap-2 space-y-3">
          <div>
            <Label className="mb-1 text-xs font-medium text-gray-700">Recipients</Label>
            <p className="mb-2 text-xs text-gray-500">Defaults to everyone with access to this matter.</p>
            <MultiSelect values={recipientIds} onValuesChange={setRecipientIds}>
              <MultiSelectTrigger className="w-full">
                <MultiSelectValue placeholder={isLoadingUsers ? 'Loading users…' : 'Select recipients…'} />
              </MultiSelectTrigger>
              <MultiSelectContent search={{ placeholder: 'Search users…', emptyMessage: 'No users found.' }}>
                {(users ?? []).map((u) => (
                  <MultiSelectItem key={u.id} value={u.id}>
                    {u.email}
                  </MultiSelectItem>
                ))}
              </MultiSelectContent>
            </MultiSelect>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>

          <Button type="button" variant="primary" onClick={handleOpenOutlook}>
            <Icon className="vscode-icons--file-type-outlook" />
            Share via Outlook
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
